import React, { useEffect, useMemo, useState } from "react";
import DaterangePicker from "../../common/Daterangepicker";
import { LocationOn } from "@mui/icons-material";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useSelector } from "react-redux";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DashboardSkeleton from "../../common/DashboardSkeleton";

const Fleetutilization = () => {
  const cities = useSelector((state) => state?.cities?.citiesData);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  let token = localStorage.getItem("token");
  const [fleetUtllizationData, setFleetUtilizationData] = useState([]);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectionRange, setSelectionRange] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    key: "selection",
  });
  const vehicleStatus =
    JSON.parse(localStorage.getItem("catalogValues"))?.vehicleStatus || {};
  const [copiedField, setCopiedField] = useState(null);
  const [tablePage, setTablePage] = useState(0);
  const tableRowsPerPage = 10;
  const [city, setCity] = useState("1");
  const [hub, setHub] = useState("1");
  const [getAllHubsListData, setGetAllHubsListData] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);

  const getAllHubsList = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/hubs?limit=1000`);
      const data = await response.json();
      if (data?.statusCode === "0100")
        setGetAllHubsListData(data?.entities?.rows);
    } catch (error) {
      console.error("Error fetching City Names:", error);
    }
  };

  function formatDateForApi(date, type) {
    if (!date) return "";
    const pad = (n) => (n < 10 ? "0" + n : n);
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    let hours = type === "start" ? "00" : "23";
    let minutes = type === "start" ? "00" : "59";
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  const getVehicleUtlizationData = async (cityId = city, hubId = hub) => {
    const formattedStartDate = formatDateForApi(
      selectionRange?.startDate,
      "start",
    );
    const formattedEndDate = formatDateForApi(selectionRange?.endDate, "end");
    setLoading(true);
    try {
      let query = `startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
      if (cityId) query += `&cityId=${cityId}`;
      if (hubId) query += `&hubId=${hubId}`;
      const response = await fetch(
        `${apiUrl}/api/dashboardreports/vehicle-utilization-dashboard?${query}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );
      const data = await response.json();
      if (data?.statusCode === "0100") {
        setLoading(false);
        setFleetUtilizationData(data?.dataValues?.dataList);
      } else {
        setLoading(false);
      }
      return data;
    } catch (error) {
      setLoading(false);
      return console.error("Error fetching get vehicle listings:", error);
    }
  };

  useEffect(() => {
    getVehicleUtlizationData();
    getAllHubsList();
  }, []);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return (
      "₹" +
      parseFloat(amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })
    );
  };

  const filteredData = useMemo(() => {
    if (!fleetUtllizationData) return [];
    const filtered = fleetUtllizationData?.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.licensePlate?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    // Apply sorting
    if (sortConfig.key) {
      return [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [fleetUtllizationData, searchQuery, sortConfig]);

  const paginatedTableData = filteredData.slice(
    tablePage * tableRowsPerPage,
    (tablePage + 1) * tableRowsPerPage,
  );

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const exportCSV = () => {
    const headers = [
      "Vehicle",
      "Plate",
      "Type",
      "Status",
      "Total Revenue",
      "Active days",
      "ADR/Day",
      "Avg Duration",
      "Bookings",
    ];
    const rows = filteredData.map((row) => [
      row?.name || "-",
      row?.licensePlate || "-",
      row?.bodyTypeText || "-",
      row?.status === vehicleStatus?.inverted?.active ? "Active" : "Inactive",
      row?.totalRevenue || 0,
      row?.totalDaysOperated || 0,
      row?.averageRevenuePerDay || 0,
      row?.avgDuration || 0,
      row?.bookingCount || "-",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vehicle_revenue_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const data = fleetUtllizationData.map((data) => ({
    name: data?.name,
    revenue: data?.totalRevenue,
  }));

  return (
    <div>
      <div className="flex gap-4 justify-end w-full my-4">
        {/* <h1 className="text-2xl mt-2">Fleet Utilization</h1> */}

        <div className="flex gap-4 justify-between w-full">
          <div className="self-stretch p-5 border-b-[0.80px] border-gray-200 inline-flex justify-between items-center">
            <div className="w-96 h-10 max-w-96 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                    stroke="#9CA3AF"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.9995 13.9995L11.1328 11.1328"
                    stroke="#9CA3AF"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search vehicles by name or plate number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-9 pr-4 py-2.5 rounded-[10px] outline outline-[0.80px] outline-offset-[-0.80px] outline-gray-300 text-sm focus:outline-indigo-500 transition-all"
              />
            </div>
            <div className="pl-4 flex justify-start items-start relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="px-4 py-2.5 bg-indigo-600 rounded-[10px] flex justify-start items-center gap-2 hover:bg-indigo-700 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 12V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V12"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="text-white text-sm font-semibold">Export</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 4L6 8L10 4"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {showExportMenu && (
                <div className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 min-w-[140px]">
                  <button
                    onClick={exportCSV}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M7 1.5V8.5M7 8.5L4.5 6M7 8.5L9.5 6"
                        stroke="#374151"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M1.5 10.5V11.5C1.5 12.0523 1.94772 12.5 2.5 12.5H11.5C12.0523 12.5 12.5 12.0523 12.5 11.5V10.5"
                        stroke="#374151"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    Export CSV
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <DaterangePicker
              selectionRange={selectionRange}
              setSelectionRange={setSelectionRange}
              style={-10}
            />
            <select
              className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            >
              {cities &&
                Object.entries(cities).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
            </select>
            <select
              className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
              value={hub}
              onChange={(e) => setHub(e.target.value)}
            >
              {getAllHubsListData &&
                getAllHubsListData.map((item, index) => (
                  <option key={index} value={item?.id}>
                    {item?.hubName}
                  </option>
                ))}
            </select>

            <button
              onClick={() => {
                getVehicleUtlizationData();
              }}
              className="p-2 bg-blue-400"
            >
              Search
            </button>
            <button
              onClick={() => {
                setCity("1");
                setHub("1");
                getVehicleUtlizationData("1", "1");
              }}
              className="p-2 bg-red-400"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <>
          <DashboardSkeleton />
        </>
      ) : (
        <>
          <div className="bg-white my-4">
            <div className="flex justify-between px-2">
              {" "}
              <h1 className="text-lg font-semibold p-4">
                Vehicle wise Revenue Graph
              </h1>
              <div className="mt-4">
                {visible ? (
                  <VisibilityOffIcon
                    onClick={() => {
                      setVisible(false);
                    }}
                  />
                ) : (
                  <VisibilityIcon
                    onClick={() => {
                      setVisible(true);
                    }}
                  />
                )}
              </div>
            </div>

            {visible && (
              <LineChart
                width="95vw"
                height={300}
                data={data}
                accessibilityLayer
              >
                <XAxis dataKey="name" interval={9} />
                <YAxis />
                <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                <Line
                  dot={{ fill: "red", r: 5 }}
                  type="monotone"
                  dataKey="revenue"
                  stroke="#120d72"
                />
                <Tooltip />
              </LineChart>
            )}
          </div>

          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th
                  className="p-4 cursor-pointer hover:text-indigo-600"
                  onClick={() => requestSort("name")}
                >
                  Vehicle Name{" "}
                  {sortConfig.key === "name" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </th>
                {/* <th
              className="p-4 cursor-pointer hover:text-indigo-600"
              onClick={() => requestSort("licensePlate")}
            >
              License Plate{" "}
              {sortConfig.key === "licensePlate" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th> */}
                <th className="p-4">Type</th>
                {/* <th
              className="p-4 cursor-pointer hover:text-indigo-600"
              onClick={() => requestSort("status")}
            >
              Status{" "}
              {sortConfig.key === "isTargetMet" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th> */}
                <th
                  className="p-4 cursor-pointer hover:text-indigo-600"
                  onClick={() => requestSort("totalRevenue")}
                >
                  Total Revenue{" "}
                  {sortConfig.key === "totalRevenue" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </th>
                {/* <th
              className="p-4 cursor-pointer hover:text-indigo-600"
              onClick={() => requestSort("averageRevenuePerDay")}
            >
              ADR/Day{" "}
              {sortConfig.key === "averageRevenuePerDay" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th> */}
                <th
                  className="p-4 cursor-pointer hover:text-indigo-600"
                  onClick={() => requestSort("avgDuration")}
                >
                  Avg Duration{" "}
                  {sortConfig.key === "avgDuration" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </th>
                {/* <th
              className="p-4 cursor-pointer hover:text-indigo-600"
              onClick={() => requestSort("achivement")}
            >
              Target achieved{" "}
              {sortConfig.key === "achivement" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th> */}
                <th
                  className="p-4 cursor-pointer hover:text-indigo-600"
                  onClick={() => requestSort("totalDaysOperated")}
                >
                  Active Days{" "}
                  {sortConfig.key === "totalDaysOperated" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </th>
                <th className="p-4">Idle Days</th>
                <th className="p-4">Occupancy</th>
                <th className="p-4">18-day status</th>
                <th
                  className="p-4 cursor-pointer hover:text-indigo-600"
                  onClick={() => requestSort("bookingCount")}
                >
                  Bookings{" "}
                  {sortConfig.key === "bookingCount" &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </th>
                {/* <th className="p-4">Avg gap(Days)</th> */}
              </tr>
            </thead>
            <tbody className="text-sm font-medium text-gray-700 divide-y divide-gray-100">
              {paginatedTableData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center p-8 text-gray-400">
                    No Data recorded for chosen timestamp.
                  </td>
                </tr>
              ) : (
                paginatedTableData.map((row) => {
                  const activeDays = row.totalDaysOperated || 0;
                  const idleDays = Math.max(0, 30 - activeDays);
                  const occupancy =
                    activeDays > 0 ? Math.round((activeDays / 30) * 100) : 0;
                  const avgGap =
                    row.bookingCount > 0
                      ? (activeDays / row.bookingCount).toFixed(1)
                      : 0;
                  const daysRemaining = row.daysRemaining || 18;

                  let statusLabel = "On track";
                  let statusColor = "#006240"; // Green

                  const daysRun = row.totalDaysOperated || 0;
                  const totalDaysInPeriod = 30; // Since your date range is 1 month
                  const occupancyPercent = Math.round(
                    (daysRun / totalDaysInPeriod) * 100,
                  );

                  if (daysRun === 0) {
                    statusLabel = "Never Used";
                    statusColor = "#AB0000"; // Red
                  } else if (occupancyPercent < 20) {
                    statusLabel = "Rarely Used";
                    statusColor = "#AB0000"; // Red
                  } else if (occupancyPercent < 40) {
                    statusLabel = "Underused";
                    statusColor = "#FF8C00"; // Orange
                  } else if (occupancyPercent < 60) {
                    statusLabel = "Moderately Used";
                    statusColor = "#0D2E9B"; // Blue
                  } else if (occupancyPercent < 80) {
                    statusLabel = "Well Used";
                    statusColor = "#006240"; // Green
                  } else if (occupancyPercent >= 80) {
                    statusLabel = "Highly Utilized";
                    statusColor = "#006240"; // Green
                  }
                  return (
                    <tr
                      key={row.id}
                      className="bg-white hover:bg-slate-50/80 transition duration-150"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3 group">
                          <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-bold text-xs flex items-center justify-center flex-shrink-0">
                            {row?.name?.charAt(0) || "V"}
                          </div>
                          <div>
                            <div className="font-bold text-[15px] text-gray-900 max-w-[180px] truncate flex items-center gap-1">
                              {row.name || "-"}
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    row.name || "",
                                    `name_${row.id}`,
                                  )
                                }
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <path
                                    d="M2 2H7V7H2V2Z"
                                    stroke="#6B7280"
                                    strokeWidth="1"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M5 5H10V10H5V5Z"
                                    stroke="#6B7280"
                                    strokeWidth="1"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                              {copiedField === `name_${row.id}` && (
                                <span className="text-[8px] text-green-600 font-normal">
                                  ✓
                                </span>
                              )}
                            </div>
                            <div className="text-[12px] text-gray-400 flex items-center gap-0.5 mt-0.5">
                              <LocationOn sx={{ fontSize: 10 }} />
                              {row.cityText || "Unmapped"}
                            </div>
                            <div className="flex items-center gap-2 group">
                              <span className="font-mono text-[16px] font-bold text-slate-600 bg-slate-100 rounded-md px-2.5 py-1 border border-slate-200 tracking-wider">
                                {row.licensePlate || "UNREGISTERED"}
                              </span>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    row.licensePlate || "",
                                    `plate_${row.id}`,
                                  )
                                }
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <path
                                    d="M2 2H7V7H2V2Z"
                                    stroke="#6B7280"
                                    strokeWidth="1"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M5 5H10V10H5V5Z"
                                    stroke="#6B7280"
                                    strokeWidth="1"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                              {copiedField === `plate_${row.id}` && (
                                <span className="text-[8px] text-green-600 font-normal">
                                  ✓
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* <td className="p-4">
                    
                  </td> */}
                      <td className="p-4 text-gray-600">
                        {row.bodyTypeText || "SUV"}
                      </td>
                      {/* <td className="p-4">
                    <div
                      className={`w-24 px-2 py-1 rounded-lg flex items-center gap-2 ${
                        row.status === 179
                          ? "bg-green-400/25 outline outline-1 outline-green-500/70"
                          : "bg-sky-300/25 outline outline-1 outline-sky-400/60"
                      }`}
                    >
                      {row.status === 179 ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          className="flex-shrink-0"
                        >
                          <g clip-path="url(#clip0_829_567)">
                            <path
                              d="M6.82867 10.876L4 8.04668L4.94267 7.10402L6.82867 8.98935L10.5993 5.21802L11.5427 6.16135L6.82867 10.876Z"
                              fill="#1A1A1A"
                            />
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M0.666656 8.00008C0.666656 3.95008 3.94999 0.666748 7.99999 0.666748C12.05 0.666748 15.3333 3.95008 15.3333 8.00008C15.3333 12.0501 12.05 15.3334 7.99999 15.3334C3.94999 15.3334 0.666656 12.0501 0.666656 8.00008ZM7.99999 14.0001C7.21206 14.0001 6.43184 13.8449 5.70389 13.5434C4.97594 13.2418 4.3145 12.7999 3.75735 12.2427C3.2002 11.6856 2.75824 11.0241 2.45671 10.2962C2.15518 9.56823 1.99999 8.78801 1.99999 8.00008C1.99999 7.21215 2.15518 6.43193 2.45671 5.70398C2.75824 4.97603 3.2002 4.31459 3.75735 3.75744C4.3145 3.20029 4.97594 2.75833 5.70389 2.4568C6.43184 2.15528 7.21206 2.00008 7.99999 2.00008C9.59129 2.00008 11.1174 2.63222 12.2426 3.75744C13.3678 4.88266 14 6.40878 14 8.00008C14 9.59138 13.3678 11.1175 12.2426 12.2427C11.1174 13.3679 9.59129 14.0001 7.99999 14.0001Z"
                              fill="#1A1A1A"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_829_567">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          className="flex-shrink-0"
                        >
                          <g clip-path="url(#clip0_829_568)">
                            <path
                              d="M8 4L8 8.66667M8 11.3333V12"
                              stroke="#1A1A1A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M0.666656 8.00008C0.666656 3.95008 3.94999 0.666748 7.99999 0.666748C12.05 0.666748 15.3333 3.95008 15.3333 8.00008C15.3333 12.0501 12.05 15.3334 7.99999 15.3334C3.94999 15.3334 0.666656 12.0501 0.666656 8.00008ZM7.99999 14.0001C7.21206 14.0001 6.43184 13.8449 5.70389 13.5434C4.97594 13.2418 4.3145 12.7999 3.75735 12.2427C3.2002 11.6856 2.75824 11.0241 2.45671 10.2962C2.15518 9.56823 1.99999 8.78801 1.99999 8.00008C1.99999 7.21215 2.15518 6.43193 2.45671 5.70398C2.75824 4.97603 3.2002 4.31459 3.75735 3.75744C4.3145 3.20029 4.97594 2.75833 5.70389 2.4568C6.43184 2.15528 7.21206 2.00008 7.99999 2.00008C9.59129 2.00008 11.1174 2.63222 12.2426 3.75744C13.3678 4.88266 14 6.40878 14 8.00008C14 9.59138 13.3678 11.1175 12.2426 12.2427C11.1174 13.3679 9.59129 14.0001 7.99999 14.0001Z"
                              fill="#1A1A1A"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_829_568">
                              <rect width="16" height="16" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      )}
                      <span className="text-sm font-semibold text-[#1A1A1A]">
                        {row.status === 179 ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </td> */}
                      <td className="p-4 font-black text-gray-900">
                        {formatCurrency(row.totalRevenue || 0)}
                      </td>
                      {/* <td className="p-4 font-medium text-gray-700">
                    {formatCurrency(row.averageRevenuePerDay || 0)}
                  </td> */}
                      <td className="p-4 font-medium text-gray-700">
                        {row.avgDuration || 0} days
                      </td>
                      {/* <td className="p-4">
                    <div className="flex items-center gap-1">
                      <div className="w-20 bg-indigo-100 rounded-full h-2">
                        <div
                          className="h-2 bg-indigo-600 rounded-full"
                          style={{
                            width: `${Math.min(row.achivement || 0, 100)}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs font-medium text-neutral-500">
                        {Math.round(row.achivement || 0)}%
                      </span>
                    </div>
                  </td> */}
                      <td className="p-4 font-semibold text-gray-700">
                        {activeDays}
                      </td>
                      <td className="p-4 font-medium text-gray-600">
                        {idleDays}
                      </td>
                      <td className="p-4 font-medium text-gray-700">
                        {occupancy}%
                      </td>
                      <td className="p-4">
                        <div
                          className={`w-28 px-2.5 py-1 rounded-lg flex items-center gap-2 bg-${statusColor === "#006240" ? "emerald" : statusColor === "#0D2E9B" ? "blue" : "red"}-50 outline outline-[0.80px] outline-offset-[-0.80px] outline-stone-200`}
                        >
                          <div
                            className={`w-2 h-1.5 rounded-full`}
                            style={{ backgroundColor: statusColor }}
                          />
                          <span
                            className="text-xs font-medium capitalize"
                            style={{ color: statusColor }}
                          >
                            {statusLabel}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 font-bold text-indigo-600">
                        {row.bookingCount}
                      </td>
                      {/* <td className="p-4 font-medium text-gray-700">{avgGap}</td> */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <div className="self-stretch px-6 py-4 border-t-[0.80px] border-gray-200 inline-flex justify-between items-center">
            <div className="text-gray-500 text-sm font-normal">
              Showing {tablePage * tableRowsPerPage + 1} to{" "}
              {Math.min(
                (tablePage + 1) * tableRowsPerPage,
                filteredData.length,
              )}{" "}
              of {filteredData.length} vehicles
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={tablePage === 0}
                onClick={() => setTablePage((p) => p - 1)}
                className="p-2 rounded-[10px] outline outline-[0.80px] outline-gray-300 disabled:opacity-50 hover:bg-gray-50"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 4L6 8L10 12"
                    stroke="#1A1A1A"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <div className="text-gray-700 text-sm font-medium">
                Page {tablePage + 1} of{" "}
                {Math.ceil(filteredData.length / tableRowsPerPage)}
              </div>
              <button
                disabled={
                  (tablePage + 1) * tableRowsPerPage >= filteredData.length
                }
                onClick={() => setTablePage((p) => p + 1)}
                className="p-2 rounded-[10px] outline outline-[0.80px] outline-gray-300 disabled:opacity-50 hover:bg-gray-50"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="#1A1A1A"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Fleetutilization;
